import React, { FunctionComponent, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import classNames from 'classnames/bind'
import { Spring } from 'react-spring/renderprops'
import { withWidthLowerObserver } from '../../../../misc/WidthObserver.hoc'
import { breakMedium } from '../../../../misc/responsive'

import { Chip } from '../Chip/Chip.component'
import { IconChip } from '../IconChip'
import { MenuChipProps } from './MenuChip.model'

import styles from './MenuChip.module.scss'
import { Theme as ButtonThemes } from '../../../../design-system/themes/button.model'
import { IconButton } from '../../buttons'
import { GridLayout } from '../../layout'
import { DocumentTemplateEditorVariantType } from '../../../../model/DocumentTemplate'
import { dmpIsReadySelector, dmpIsSendingSelector } from '../../../../store/domain/dmp'
import { useCurrentPatient } from '../../../../hooks/utils'

const stylesCx = classNames.bind(styles)

const MenuChipComponent: FunctionComponent<MenuChipProps> = ({
  icon,
  label,
  collapsed,
  colorPreset = 'dark',
  borderColor,
  onView,
  onPrint,
  onSendToDMP,
  onDeleteFromDMP,
  onDelete,
  onTitleSave,
  onLock,
  onConfigure,
  onRenewal,
  downloadConfig,
  isLowerThanBreakpoint,
  isLock,
  disablePrintDownload,
  disableLocker,
  type,
  isPrinting,
  currentPrintingItems,
  isDownloading,
  currentDownloadingItems,
  id,
  ...restProps
}) => {
  const [title, setTitle] = useState(label)
  const [editMode, setEditMode] = useState(false)
  const isDmpAvailable = useSelector(dmpIsReadySelector)
  const dmpIsSending = useSelector(dmpIsSendingSelector)
  const { currentPatient } = useCurrentPatient()

  useEffect(() => {
    setTitle(label)
  }, [label])

  function handleSaveTitle() {
    setEditMode(false)
    if (onTitleSave) {
      onTitleSave(title)
    }
  }

  const actionsClasses = stylesCx({
    actions: true,
    [colorPreset]: true,
  })
  const iconButtonTheme: ButtonThemes = colorPreset === 'dark' ? 'light' : 'transparent-dark'

  const configureTestId = useMemo(
    () =>
      type === DocumentTemplateEditorVariantType.QUESTIONNAIRE
        ? 'button-menu_chip-questionnaire-setup'
        : 'button-menu_chip-setup',
    [type],
  )

  const dmpTestId = useMemo(
    () =>
      onDeleteFromDMP
        ? 'button-menu_chip-dmp_available_document-options-show'
        : 'button-menu_chip-dmp_missing_document-options-show',
    [onDeleteFromDMP],
  )

  const lockTestId = useMemo(
    () =>
      (disableLocker && 'button-menu_chip-unlock-impossible-display') ||
      `button-menu_chip-${isLock ? 'unlock' : 'lock'}-display`,
    [disableLocker, isLock],
  )

  const isItemPrinting = useMemo(() => {
    return (isPrinting && currentPrintingItems?.some((item) => item.id === id)) || false
  }, [currentPrintingItems, id, isPrinting])
  const isItemDownloading = useMemo(() => {
    return (isDownloading && currentDownloadingItems?.some((item) => item.id === id)) || false
  }, [currentDownloadingItems, id, isDownloading])

  return (
    <div className={styles.container}>
      <div className={styles.chipContainer}>
        {icon && (
          <IconChip
            icon={icon}
            label={title}
            colorPreset={colorPreset}
            borderColor={borderColor}
            editMode={editMode}
            onTitleEdit={setTitle}
            onTitleSave={handleSaveTitle}
            {...restProps}
          />
        )}
        {!icon && <Chip colorPreset={colorPreset} label={title} {...restProps} />}
      </div>
      <Spring
        from={{ width: collapsed ? 'auto' : 0, opacity: collapsed ? 1 : 0 }}
        to={{ width: collapsed ? 0 : 'auto', opacity: collapsed ? 0 : 1 }}
      >
        {(springProps) => (
          <div
            className={stylesCx(actionsClasses, {
              [styles.collapsed]: springProps.width === 0,
            })}
            style={springProps}
          >
            <GridLayout
              columnsTemplate="auto"
              padding={['none', 'extraSmall']}
              gap="extraSmall"
              shouldShrinkOnMobile={false}
              flow="column"
              height="100%"
              alignColumns="center"
            >
              {onView && (
                <IconButton
                  icon="eye"
                  title="Visualiser"
                  onClick={onView}
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  testId="button-menu_chip-selected_patient-remove"
                  noBorder
                />
              )}

              {onPrint && (
                <IconButton
                  icon="print"
                  title="Imprimer (Ctrl+P)"
                  onClick={onPrint}
                  isLoading={isItemPrinting}
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  noBorder
                  disabled={disablePrintDownload || isPrinting}
                  testId="button-menu_chip-print"
                />
              )}

              {downloadConfig && (
                <IconButton
                  icon="save"
                  title="Télécharger (Ctrl+Shift+S)"
                  onClick={Array.isArray(downloadConfig) ? undefined : downloadConfig}
                  options={Array.isArray(downloadConfig) ? downloadConfig : undefined}
                  theme={iconButtonTheme}
                  isLoading={isItemDownloading}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  noBorder
                  disabled={disablePrintDownload || isDownloading}
                  testId="button-menu_chip-download"
                  downloadPDFCTA={true}
                />
              )}

              {onSendToDMP && (
                <IconButton
                  icon={onDeleteFromDMP ? 'dmpCheck' : 'dmpCross'}
                  title={
                    (!currentPatient?.inseeNumber &&
                      'Le matricule INS est nécessaire pour accéder au DMP du patient') ||
                    (dmpIsSending && 'Envoi au DMP en cours, veuillez patienter...') ||
                    (!isDmpAvailable &&
                      "Chargement d'Efficience en cours, lecture de la carte CPS") ||
                    'Consulter le DMP du patient'
                  }
                  options={
                    onDeleteFromDMP
                      ? [
                          { label: 'Valider et mettre à jour', onClick: onSendToDMP },
                          { label: 'Supprimer du DMP', onClick: onDeleteFromDMP },
                        ]
                      : [{ label: 'Valider et envoyer', onClick: onSendToDMP }]
                  }
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'micro' : 'normal'}
                  noBorder
                  disabled={!isDmpAvailable}
                  testId={dmpTestId}
                />
              )}

              {onLock && (
                <IconButton
                  icon={isLock ? 'lock' : 'unlock'}
                  title={
                    (disableLocker && 'Déverrouillage impossible') ||
                    (isLock && 'Cliquer pour déverrouiller') ||
                    'Cliquer pour verrouiller'
                  }
                  onClick={onLock}
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  noBorder
                  disabled={disableLocker}
                  testId={lockTestId}
                />
              )}

              {onTitleSave && (
                <IconButton
                  icon="pencil"
                  title="Editer"
                  onClick={() => setEditMode(true)}
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  testId="button-menu_chip-title-edit"
                  noBorder
                />
              )}

              {onRenewal && (
                <IconButton
                  icon="arrowCircle"
                  title="Renouveller l'ordonnance"
                  onClick={onRenewal}
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  testId="button-menu_chip-prescription-renew"
                  noBorder
                />
              )}

              {onConfigure && (
                <IconButton
                  icon="settings"
                  title={
                    type === DocumentTemplateEditorVariantType.QUESTIONNAIRE
                      ? 'Configurer le modèle de questionnaire'
                      : 'Configurer'
                  }
                  onClick={onConfigure}
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  testId={configureTestId}
                  noBorder
                />
              )}

              {onDelete && (
                <IconButton
                  icon="trash"
                  title="Supprimer"
                  onClick={onDelete}
                  theme={iconButtonTheme}
                  size={isLowerThanBreakpoint ? 'nano' : 'micro'}
                  testId="button-menu_chip-delete"
                  noBorder
                />
              )}
            </GridLayout>
          </div>
        )}
      </Spring>
    </div>
  )
}

export const MenuChip = withWidthLowerObserver(breakMedium)(MenuChipComponent)
